// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The About panel leads with the limits, not the features.
//
// The single most damaging thing this site could do is let a reader take a
// per-farm survey estimate with a 40% relative standard error, drawn from
// eleven farms, and treat it as a fact about their own district — or take a
// broadacre finding and repeat it as a fact about Australian agriculture.

import { closeButton, openOverlay } from './overlay';
import { num, pct, fy, money } from './format';
import { SCOPE } from './theme';
import { GLOSSARY, GLOSSARY_ORDER } from './glossary';
import type { Meta } from './types';

export function openAbout(meta: Meta): void {
  openOverlay({
    panelClass: 'modal',
    label: 'About this site',
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = 'About Farm Economics';
      head.append(h, closeButton(close, 'Close the about panel'));

      const body = document.createElement('div');
      body.className = 'stack small';
      const s = meta.survey;
      const hl = meta.headline;
      const perFarm = hl.perFarm;
      const perHa = hl.perHectare;

      body.innerHTML = `
        <p>ABARES has surveyed Australian broadacre farms every year since 1990 and publishes the
        results as 132 variables across 32 agricultural regions. This site reads all of it and does
        the one thing the publication does not: it separates what the <strong>farming</strong>
        earned from what the <strong>land</strong> did, and shows how far apart those two numbers
        have grown.</p>

        <div class="note">
          <strong>What this covers, and what it does not.</strong>
          ${SCOPE}
        </div>

        <h3>The finding</h3>
        <p>Summed over ${meta.firstYear}–${meta.settledLastYear}, the average surveyed broadacre farm
        earned <strong>${money(hl.profitFeSum, { long: true })}</strong> from farming and
        <strong>${money(hl.appreciationSum, { long: true })}</strong> from its land appreciating.
        Roughly three quarters of the long-run return to farm capital has been the asset, not the
        activity.</p>
        <p>Two independent methods agree, which is why it is stated at all. Dollar-weighted over the
        period: <strong>${pct(hl.landShareDollarWeighted)}</strong>. From the gap between ABARES' two
        published rate-of-return series — no summation, no deflator —
        <strong>${pct(hl.meanRateEx, 2)}</strong> a year excluding land appreciation against
        <strong>${pct(hl.meanRateInc, 2)}</strong> including it, a
        <strong>${pct(hl.landShareRateWeighted)}</strong> share. The site says "roughly three
        quarters" and not "${pct(hl.landShareDollarWeighted)}", because a median relative standard
        error of ${s.medianRse}% does not license a decimal point.</p>

        <div class="note">
          <strong>Land is not a one-way ratchet, and this site will not let you think it is.</strong>
          Capital appreciation was <strong>negative</strong> in
          ${hl.negativeAppreciationYears.length} of the ${meta.lastYear - meta.firstYear + 1} years,
          in two clusters: ${hl.negativeAppreciationYears.join(', ')}. Across the regions it was
          negative in ${num(hl.negativeApprRegionYears)} of ${num(hl.totalRegionYears)} region-years.
        </div>

        <h3>We tried to break it</h3>
        <p>The obvious objection is that the finding is an artefact of starting in ${meta.firstYear},
        just before a land boom. It is not. Every start year from ${meta.firstYear} to 2020 produces a
        land share between <strong>${pct(hl.startYearShareMin)}</strong> and
        <strong>${pct(hl.startYearShareMax)}</strong>, and starting in 2013 — after the second bust —
        gives a <em>higher</em> share, not a lower one.${perFarm && perHa ? ` The second objection is
        that per-farm figures only rose because farms got bigger. They did get bigger
        (${perFarm.area?.toFixed(2)}×), and the divergence survives anyway: per hectare operated,
        land ran ${perHa.land?.toFixed(2)}× against receipts ${perHa.receipts?.toFixed(2)}×.` : ''}
        Both tests are built into the site as controls rather than described as caveats.</p>

        <h3>What the finding does NOT say</h3>
        <ul>
          <li><strong>It does not say farming does not pay.</strong> The national operating return
          averaged ${pct(hl.meanRateEx, 2)} a year and was positive in most years. Only
          ${hl.regionsNegative.length} of ${meta.regionCount} regions average a loss.</li>
          <li><strong>It does not say farmers banked those gains.</strong> Capital appreciation is an
          unrealised paper gain on a working asset. It cannot be eaten, and realising it generally
          means ending the farming.</li>
          <li><strong>It is about the return to farm CAPITAL, not household income.</strong> Off-farm
          wages and non-farm income exist in this data and are kept separate.</li>
          <li><strong>It says nothing about ${meta.provisionalYear}.</strong> That year is
          provisional — ABARES carries the farm population forward almost unchanged
          (${num(49241)} to ${num(49242)}) — so it is drawn hatched and excluded from every trend,
          mean, ranking and fitted line here.</li>
        </ul>

        <h3>These are survey estimates, and this site says so on every mark</h3>
        <p>Every figure comes from a sample of farms, not from all of them, and carries a relative
        standard error. This is not a footnote here: it is drawn.</p>
        <ul>
          <li>Median relative standard error: <strong>${s.medianRse}%</strong>.
          <strong>${pct(s.pctOver25)}</strong> of cells exceed ABARES' own 25% "use with caution"
          threshold and <strong>${pct(s.pctOver50)}</strong> exceed its 50% "unreliable" threshold.</li>
          <li>Behind each region-year sits anywhere from <strong>${s.sample.min}</strong> to
          <strong>${s.sample.max}</strong> real farms; the median is ${s.sample.median}.</li>
          <li>Every value is placed in one of five evidence tiers, combining its error and its sample
          size, and the tier changes how the mark is drawn. Weak estimates are outline-only and
          excluded from every ranking, average and total. Estimates with no basis show their position
          but never their magnitude. Where ABARES published nothing, lines break — nothing is
          interpolated or filled with zero.</li>
          <li>Of ${num(meta.cellCount)} regional cells:
          ${num(s.tierHist[1])} firm, ${num(s.tierHist[2])} fair, ${num(s.tierHist[3])} weak,
          ${num(s.tierHist[4])} with no basis, and ${num(s.tierHist[5])} where no estimate exists.</li>
        </ul>
        <p>Unreliable estimates are never deleted. Suppressing them would leave a hole across northern
        Australia that reads as "nothing happens there" rather than "we barely measure there" — and
        where the survey is thin is itself one of the more interesting things in this data.</p>

        <h3>Three things this site refuses to do</h3>
        <ul>
          <li><strong>It never recalculates a rate of return.</strong> Dividing profit by capital
          fails to reproduce ABARES' published series in 20–56% of region-years depending on which
          capital figure you use. Every rate here is ABARES' own, used as published.</li>
          <li><strong>It never crosses region with industry.</strong> The regional file has no
          industry dimension — every region is all-broadacre combined — so "beef in the Kimberley"
          is a question this data cannot answer, and the site does not pretend otherwise. Industry
          appears only at national and state level.</li>
          <li><strong>It never forecasts, and never reaches for a superlative.</strong> A survey
          with these error bars, ending on a provisional year, cannot support one — so no figure
          here is described as the largest, the smallest or the first of anything.</li>
        </ul>

        <h3>Attribution and licensing</h3>
        <p>Farm survey data © Commonwealth of Australia (ABARES, Department of Agriculture, Fisheries
        and Forestry), used under
        <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>
        as stated at <a href="https://www.agriculture.gov.au/copyright" target="_blank" rel="noopener">agriculture.gov.au/copyright</a>.
        Region boundaries are the department's own AAGIS region file, simplified for the web.
        ABARES does not endorse this site or any conclusion drawn here.</p>
        <p>Data as at <strong>${meta.asAt}</strong>, covering ${fy(meta.firstYear)} to
        ${fy(meta.lastYear)}. Refreshed yearly, matching ABARES' publication cycle.
        This is an independent project and is not affiliated with ABARES or the department.</p>
        <p><strong>Nothing here is financial, agronomic or valuation advice.</strong> It describes
        what a national survey recorded about groups of farms in the past. It cannot tell you what
        any individual farm is worth or what it will earn.</p>

        <h3>Glossary</h3>
        <dl class="stack">
          ${GLOSSARY_ORDER.map((k) => {
            const t = GLOSSARY[k];
            return t ? `<div><dt><strong>${t.term}</strong></dt><dd class="muted">${t.short}</dd></div>` : '';
          }).join('')}
        </dl>
      `;

      panel.append(head, body);
    },
  });
}
